import type { MemberId, Ticket, TicketEvent } from './types';
import { countCharacters, validateDraft } from './validation';

export type MergeResult =
  | { kind: 'duplicate' }
  | { kind: 'stale'; ticket: Ticket }
  | { kind: 'applied'; ticket: Ticket }
  | { kind: 'conflict'; local: Ticket; incoming: Ticket };

export function mergeTicket(local: Ticket, incoming: Ticket): MergeResult {
  assertCompatibleTickets(local, incoming);
  validateTicketGraph(local);
  validateTicketGraph(incoming);

  const events = mergeEvents(local.events, incoming.events);
  validateEventGraph(events);
  const localIds = new Set(local.events.map((event) => event.id));
  const incomingIds = new Set(incoming.events.map((event) => event.id));

  if (local.headEventId === incoming.headEventId && sameIds(localIds, incomingIds)) {
    return { kind: 'duplicate' };
  }

  if (isAncestorFromEvents(events, incoming.headEventId, local.headEventId)) {
    return { kind: 'stale', ticket: local };
  }

  if (isAncestorFromEvents(events, local.headEventId, incoming.headEventId)) {
    return { kind: 'applied', ticket: mergedSnapshot(incoming, events, local.imageId) };
  }

  if (local.headEventId === incoming.headEventId) {
    return { kind: 'applied', ticket: mergedSnapshot(incoming, events, local.imageId) };
  }

  return { kind: 'conflict', local, incoming };
}

export function resolveConflict(
  conflict: Extract<MergeResult, { kind: 'conflict' }>,
  keep: 'local' | 'incoming',
  actor: MemberId,
  at: string,
  eventId: string,
): Ticket {
  assertCompatibleTickets(conflict.local, conflict.incoming);
  validateTicketGraph(conflict.local);
  validateTicketGraph(conflict.incoming);

  const events = mergeEvents(conflict.local.events, conflict.incoming.events);
  if (events.some((event) => event.id === eventId)) throw new Error('resolution event already exists');

  const chosen = keep === 'local' ? conflict.local : conflict.incoming;
  const resolution: TicketEvent = {
    id: eventId,
    parentIds: [conflict.local.headEventId, conflict.incoming.headEventId],
    actor,
    action: 'conflict_resolved',
    at,
    state: chosen.state,
  };
  const resolved = {
    ...chosen,
    imageId: conflict.local.imageId ?? chosen.imageId,
    updatedAt: at,
    headEventId: eventId,
    events: [...events, resolution],
  };
  validateTicketGraph(resolved);
  return resolved;
}

export function isAncestor(ticket: Ticket, ancestorId: string, headId: string): boolean {
  return isAncestorFromEvents(ticket.events, ancestorId, headId);
}

function assertCompatibleTickets(local: Ticket, incoming: Ticket): void {
  const sharedKeys: Array<keyof Ticket> = [
    'id', 'coupleId', 'creatorId', 'recipientId', 'type', 'title', 'description',
    'outcome', 'dueDate', 'importance', 'signature', 'createdAt',
  ];
  for (const key of sharedKeys) {
    if (local[key] !== incoming[key]) throw new Error('incompatible ticket');
  }
  if (!sameStrings(local.tags, incoming.tags)) throw new Error('incompatible ticket');
}

export function validateTicketGraph(ticket: Ticket): void {
  validateTicketIdentity(ticket);
  validateEventGraph(ticket.events);
  validateEventTransitions(ticket);
  const head = ticket.events.find((event) => event.id === ticket.headEventId);
  if (!head) throw new Error('unknown head event');
  if (ticket.events.some((event) => event.parentIds.includes(ticket.headEventId))) {
    throw new Error('head event is not a graph tip');
  }
  if (ticket.events.some((event) => !isAncestorFromEvents(ticket.events, event.id, ticket.headEventId))) {
    throw new Error('event is outside head ancestry');
  }
  if (ticket.state !== head.state) throw new Error('ticket state does not match head event');
  if (ticket.updatedAt !== head.at) throw new Error('ticket updated time does not match head event');
}

function validateTicketIdentity(ticket: Ticket): void {
  if (!ticket.id || !ticket.coupleId || !ticket.creatorId || !ticket.recipientId || ticket.creatorId === ticket.recipientId) {
    throw new Error('invalid ticket identifiers');
  }
  if (Object.keys(validateDraft(ticket)).length > 0) throw new Error('invalid ticket immutable fields');
}

function validateEventTransitions(ticket: Ticket): void {
  const byId = new Map(ticket.events.map((event) => [event.id, event]));

  for (const event of ticket.events) {
    if (!event.id) throw new Error('invalid ticket identifiers');
    if (event.action === 'created') {
      if (event.actor !== ticket.creatorId || event.state !== 'pending_response' || event.at !== ticket.createdAt) {
        throw new Error('invalid ticket transition');
      }
      continue;
    }

    const parents = event.parentIds.map((parentId) => byId.get(parentId)!);
    const parent = parents[0]!;
    const valid = event.action === 'accepted'
      ? parent.state === 'pending_response' && event.actor === ticket.recipientId && event.state === 'accepted'
      : event.action === 'rejected'
        ? parent.state === 'pending_response'
          && event.actor === ticket.recipientId
          && event.state === 'rejected'
          && validRequiredReply(event.note)
        : event.action === 'submitted'
          ? parent.state === 'accepted'
            && event.actor === ticket.recipientId
            && event.state === 'pending_confirmation'
            && validOptionalReply(event.note)
          : event.action === 'confirmed'
            ? parent.state === 'pending_confirmation' && event.actor === ticket.creatorId && event.state === 'completed'
            : validConflictResolution(event.state, parents, ticket.events);

    if (!valid) throw new Error('invalid ticket transition');
  }
}

function validConflictResolution(state: TicketEvent['state'], parents: TicketEvent[], events: TicketEvent[]): boolean {
  const [left, right] = parents;
  if (!left || !right || !parents.some((candidate) => candidate.state === state)) return false;
  return !isAncestorFromEvents(events, left.id, right.id) && !isAncestorFromEvents(events, right.id, left.id);
}

function validRequiredReply(note: string | undefined): boolean {
  return note !== undefined && note.trim().length > 0 && countCharacters(note.trim()) <= 120;
}

function validOptionalReply(note: string | undefined): boolean {
  return note === undefined || countCharacters(note.trim()) <= 120;
}

function validateEventGraph(events: TicketEvent[]): void {
  if (!events.length) throw new Error('ticket event graph is empty');

  const byId = new Map<string, TicketEvent>();
  for (const event of events) {
    if (!event.id) throw new Error('invalid ticket identifiers');
    const previous = byId.get(event.id);
    if (previous && !sameEvent(previous, event)) throw new Error('conflicting event id');
    if (previous) throw new Error('duplicate event id');
    const expectedParents = event.action === 'created' ? 0 : event.action === 'conflict_resolved' ? 2 : 1;
    if (event.parentIds.length !== expectedParents || new Set(event.parentIds).size !== event.parentIds.length) {
      throw new Error('invalid event parent cardinality');
    }
    byId.set(event.id, event);
  }

  const roots = events.filter((event) => event.parentIds.length === 0);
  if (roots.length !== 1 || roots[0]?.action !== 'created') throw new Error('invalid root event');

  for (const event of events) {
    for (const parentId of event.parentIds) {
      if (!byId.has(parentId)) throw new Error('missing parent event');
    }
  }

  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (eventId: string): void => {
    if (visited.has(eventId)) return;
    if (visiting.has(eventId)) throw new Error('cyclic event graph');
    visiting.add(eventId);
    for (const parentId of byId.get(eventId)!.parentIds) visit(parentId);
    visiting.delete(eventId);
    visited.add(eventId);
  };
  for (const event of events) visit(event.id);
}

function mergeEvents(localEvents: TicketEvent[], incomingEvents: TicketEvent[]): TicketEvent[] {
  const byId = new Map<string, TicketEvent>();
  const merged: TicketEvent[] = [];
  for (const event of [...localEvents, ...incomingEvents]) {
    const previous = byId.get(event.id);
    if (previous) {
      if (!sameEvent(previous, event)) throw new Error('conflicting event id');
      continue;
    }
    byId.set(event.id, event);
    merged.push(event);
  }
  return merged;
}

function mergedSnapshot(incoming: Ticket, events: TicketEvent[], localImageId: string | null): Ticket {
  return {
    ...incoming,
    imageId: localImageId ?? incoming.imageId,
    events,
  };
}

function isAncestorFromEvents(events: TicketEvent[], ancestorId: string, headId: string): boolean {
  const byId = new Map(events.map((event) => [event.id, event]));
  const pending = [headId];
  const seen = new Set<string>();
  while (pending.length) {
    const id = pending.pop()!;
    if (id === ancestorId) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    pending.push(...(byId.get(id)?.parentIds ?? []));
  }
  return false;
}

function sameEvent(left: TicketEvent, right: TicketEvent): boolean {
  return left.id === right.id
    && left.actor === right.actor
    && left.action === right.action
    && left.at === right.at
    && left.note === right.note
    && left.state === right.state
    && sameStrings([...left.parentIds].sort(), [...right.parentIds].sort());
}

function sameIds(left: Set<string>, right: Set<string>): boolean {
  return left.size === right.size && [...left].every((id) => right.has(id));
}

function sameStrings(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}
